import { openDB } from 'idb';
import { useState, useEffect } from 'react';

/**
 * Simple storage handler
 */ var simpleStorageHandler = {
    /**
   * @inheritdoc
   */ initialize: function initialize(database, storeName) {
        database.createObjectStore(storeName);
    },
    /**
   * @inheritdoc
   */ upgrade: function upgrade(database, storeName) {
        database.deleteObjectStore(storeName);
        this.initialize(database, storeName);
    },
    /**
   * @inheritdoc
   */ replace: function(key, value) {
        return value;
    },
    /**
   * @inheritdoc
   */ revive: function(key, storeObject) {
        return storeObject;
    }
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
var __generator = undefined && undefined.__generator || function(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
};
/**
 * Cache provider factory
 */ function createCacheProvider(_) {
    return _createCacheProvider.apply(this, arguments);
}
function _createCacheProvider() {
    _createCacheProvider = _async_to_generator(function(param) {
        var dbName, storeName, _param_storageHandler, storageHandler, _param_version, version, _param_onError, onError, map, db, error, cursor, key, value;
        /**
   * Do not store as non-native errors are not serializable, other properties are optional
   * @link https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm#supported_types
   */ function isFetchInfo(state) {
            return _instanceof(state.error, Error) || state.isValidating === true || state.isLoading === true;
        }
        return __generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    dbName = param.dbName, storeName = param.storeName, _param_storageHandler = param.storageHandler, storageHandler = _param_storageHandler === void 0 ? simpleStorageHandler : _param_storageHandler, _param_version = param.version, version = _param_version === void 0 ? 1 : _param_version, _param_onError = param.onError, onError = _param_onError === void 0 ? function() {} : _param_onError;
                    map = new Map();
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        3,
                        ,
                        4
                    ]);
                    return [
                        4,
                        openDB(dbName, version, {
                            upgrade: function upgrade(upgradeDb, oldVersion) {
                                if (!oldVersion) {
                                    storageHandler.initialize(upgradeDb, storeName);
                                } else {
                                    storageHandler.upgrade(upgradeDb, storeName, oldVersion);
                                }
                            }
                        })
                    ];
                case 2:
                    db = _state.sent();
                    return [
                        3,
                        4
                    ];
                case 3:
                    error = _state.sent();
                    // Use fallback
                    onError(error);
                    return [
                        2,
                        function() {
                            return map;
                        }
                    ];
                case 4:
                    return [
                        4,
                        db.transaction(storeName, "readwrite").store.openCursor()
                    ];
                case 5:
                    cursor = _state.sent();
                    _state.label = 6;
                case 6:
                    if (!cursor) return [
                        3,
                        8
                    ];
                    key = cursor.key;
                    value = storageHandler.revive(key, cursor.value);
                    // Stale
                    if (value === undefined) {
                        cursor.delete();
                    // OK
                    } else {
                        map.set(key, value);
                    }
                    return [
                        4,
                        cursor.continue()
                    ];
                case 7:
                    cursor = _state.sent();
                    return [
                        3,
                        6
                    ];
                case 8:
                    /**
   * SWR Cache provider API
   */ return [
                        2,
                        function(globalCache) {
                            return {
                                keys: function() {
                                    return map.keys();
                                },
                                get: function(key) {
                                    return map.get(key);
                                },
                                set: function(key, value) {
                                    map.set(key, value);
                                    if (isFetchInfo(value)) {
                                        return;
                                    }
                                    var storeValue = storageHandler.replace(key, value);
                                    if (storeValue === undefined) {
                                        return;
                                    }
                                    db.put(storeName, storeValue, key).catch(onError);
                                },
                                /**
     * Used only by useSWRInfinite
     */ delete: function(key) {
                                    if (map.delete(key)) {
                                        db.delete(storeName, key).catch(onError);
                                    }
                                },
                                /**
     * Documented, but missing method type
     * @link https://swr.vercel.app/docs/advanced/cache#access-to-the-cache
     * @link https://github.com/vercel/swr/pull/1936
     */ // @ts-ignore
                                clear: function() {
                                    map.clear();
                                    db.clear(storeName);
                                }
                            };
                        }
                    ];
            }
        });
    });
    return _createCacheProvider.apply(this, arguments);
}

/**
 * Cache provider hook
 */ function useCacheProvider(props) {
    var _useState = useState(), cacheProvider = _useState[0], setCacheProvider = _useState[1];
    useEffect(function() {
        // False on mount or on dependency change
        var isSetup = true;
        createCacheProvider(props).then(function(cp) {
            return isSetup && setCacheProvider(function() {
                return cp;
            });
        });
        return function() {
            isSetup = false;
        };
    }, []);
    return cacheProvider;
}

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
/**
 * Storage value handler that wraps value in object containing timestamp
 */ var timestampStorageHandler = _extends({}, simpleStorageHandler, {
    /**
   * @inheritdoc
   */ initialize: function initialize(database, storeName) {
        var objectStore = database.createObjectStore(storeName);
        objectStore.createIndex("ts", "ts");
    },
    /**
   * @inheritdoc
   */ replace: function(key, value) {
        return {
            value: value,
            ts: Date.now()
        };
    },
    /**
   * @inheritdoc
   */ revive: function(key, storeObject) {
        return storeObject.value;
    }
});

export { createCacheProvider as default, simpleStorageHandler, timestampStorageHandler, useCacheProvider };
